import { zodResolver } from '@hookform/resolvers/zod';
import { t } from 'i18next';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  Keyboard,
  Platform,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { z } from 'zod';

import type { AuthTokenResponse } from '@/api/customers/use-create-customer';
import { useCustomerStartOtp } from '@/api/customers/use-customer-start-otp';
import { useCustomerVerifyOtp } from '@/api/customers/use-customer-verify-otp';
import { useStartOtp } from '@/api/customers/use-start-otp';
import { useVerifyOtp } from '@/api/customers/use-verify-otp';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { signIn } from '@/core';
import { getToken } from '@/core/auth/utils';
import mixpanelService from '@/core/clients/mixpanel';
import { Button, ControlledInput, showErrorMessage, Text } from '@/ui';
import { Loader } from '@/ui/loader';

const OTP_LENGTH = 6;
const RESEND_COOLDOWN = 60;

const confirmOtpFormSchema = z.object({
  code: z.string().length(OTP_LENGTH),
});

type ConfirmOtpFormType = z.infer<typeof confirmOtpFormSchema>;

interface EntryConfirmOtpProps {
  phone: string;
  onBack: () => void;
  onDone: (setupComplete: boolean) => void;
}

export function EntryConfirmOtp({
  onBack,
  onDone,
  phone,
}: EntryConfirmOtpProps) {
  const { styles } = useStyles(stylesheet);
  const [resendTimer, setResendTimer] = React.useState(RESEND_COOLDOWN);

  useEffect(() => {
    mixpanelService.track('Page Viewed', {
      'Page Name': 'Verify Otp',
    });
  }, []);

  const {
    handleSubmit,
    control,
    trigger,
    setValue,
    setError,
    clearErrors,
    formState: { isValid },
  } = useForm<ConfirmOtpFormType>({
    resolver: zodResolver(confirmOtpFormSchema),
  });

  const token = getToken();

  const { mutate: verifyOtp, isPending: isVerifyingOtp } = useVerifyOtp();
  const { mutate: startOtp, isPending: isStartingOtp } = useStartOtp();
  const { mutate: verifyCustomerOtp, isPending: isVerifyingCustomerOtp } =
    useCustomerVerifyOtp();
  const { mutate: startCustomerOtp, isPending: isStartingCustomerOtp } =
    useCustomerStartOtp();

  useEffect(() => {
    if (resendTimer <= 0) return;

    const timeout = setTimeout(() => {
      setResendTimer((current) => current - 1);
    }, 1000);

    return () => clearTimeout(timeout);
  }, [resendTimer]);

  const canResend = resendTimer <= 0;

  const handleOtpSubmit = (data: ConfirmOtpFormType) => {
    mixpanelService.track('Button Clicked', {
      'Button Name': 'Verify Otp Code',
    });

    if (token) {
      verifyCustomerOtp(
        { phone, otp: data.code },
        {
          // This branch attaches a phone to an already-authenticated
          // customer, which only happens after finish-signup already
          // saved the rest of the profile, so setup is complete once
          // the phone is verified.
          onSuccess: () => {
            onDone(true);
          },
          onError: (e) => {
            setError('code', { type: 'manual', message: e.message });
          },
        }
      );
      return;
    }

    verifyOtp(
      {
        phone,
        otp: data.code,
      },
      {
        onSuccess: (respData: AuthTokenResponse) => {
          signIn(respData.data.token);
          onDone(respData.data.setupComplete);
        },
        onError: (e) => {
          setError('code', { type: 'manual', message: e.message });
        },
      }
    );
  };

  const handleResendOtp = () => {
    if (!canResend) return;

    if (token) {
      startCustomerOtp(
        { phone },
        {
          onSuccess: () => setResendTimer(RESEND_COOLDOWN),
          onError: (e) => showErrorMessage(e.message),
        }
      );
      return;
    }

    startOtp(
      { phone },
      {
        onSuccess: () => setResendTimer(RESEND_COOLDOWN),
        onError: (e) => showErrorMessage(e.message),
      }
    );
  };

  if (
    isStartingOtp ||
    isVerifyingOtp ||
    isStartingCustomerOtp ||
    isVerifyingCustomerOtp
  ) {
    return (
      <View style={styles.loaderContainer}>
        <Loader style={{ width: 150, height: 150 }} />
      </View>
    );
  }

  const resendTimerLabel = `${Math.floor(resendTimer / 60)}:${String(
    resendTimer % 60
  ).padStart(2, '0')}`;

  return (
    <TouchableWithoutFeedback
      onPress={() => {
        if (Platform.OS !== 'web') {
          Keyboard.dismiss();
        }
      }}
    >
      <View style={styles.card}>
        <NavigationButton
          type={NavigationButtonTypes.back_with_text}
          onPress={onBack}
        />

        <View style={styles.titleContainer}>
          <Text tx="entry.otp.title" type="title1" />
          <Text type="bodyRegular" style={styles.cardSubtitle}>
            {t('entry.otp.bodyPrefix', { length: OTP_LENGTH })}
            <Text type="bodyRegular" style={styles.phoneValue}>
              {`${phone}.`}
            </Text>
          </Text>
        </View>

        <ControlledInput
          control={control}
          name="code"
          label={t('entry.otp.codeLabel')}
          placeholder={t('entry.otp.codePlaceholder')}
          keyboardType="number-pad"
          textContentType="oneTimeCode"
          onChangeText={(text) => {
            clearErrors('code');
            setValue('code', text);
            trigger('code');
          }}
        />

        <Button
          tx="entry.otp.verifyCode"
          type="primary"
          onPress={handleSubmit(handleOtpSubmit)}
          disabled={!isValid}
        />

        {canResend ? (
          <Text type="caption1" style={styles.cardSubtitle}>
            {t('entry.otp.resendPrompt')}
            <Text
              type="caption1"
              style={[styles.cardSubtitle, styles.resendLink]}
              onPress={handleResendOtp}
            >
              {t('entry.otp.resendLink')}
            </Text>
          </Text>
        ) : (
          <Text
            tx="entry.otp.resendCountdown"
            type="caption1"
            style={styles.cardSubtitle}
            values={{ time: resendTimerLabel }}
          />
        )}
      </View>
    </TouchableWithoutFeedback>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  loaderContainer: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background_1,
  },
  card: {
    width: '100%',
    maxWidth: 440,
    paddingHorizontal: 18,
    gap: 16,
  },
  titleContainer: {
    gap: 8,
    marginTop: 8,
  },
  cardSubtitle: {
    color: theme.colors.text.secondary,
  },
  phoneValue: {
    color: theme.colors.text.primary,
  },
  resendLink: {
    textDecorationLine: 'underline',
  },
}));
